import React, { useState, useEffect } from 'react';
import { db, auth, storage } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  orderBy,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaTrash, FaEye, FaCalendar, FaCheckCircle, FaBook, FaPrint, FaShare, FaShareAlt } from 'react-icons/fa';
import { resolveImageUrl, warmResolvedImageUrls } from '../utils/urlResolver';
import KidBackground from '../ui/backgrounds/KidBackground';
import BackButton from '../components/BackButton';
import { Home, LayoutDashboard, LogOut } from 'lucide-react';
import splashImage from '../assets/splash.png';
import { useAuth } from '../context/AuthContext';
import LearningSpaceShareModal from '../components/LearningSpaceShareModal';
import SmartImage from '../components/SmartImage';

const dailyDashboardSnapshotKey = (uid) => `daily-activities:dashboard:${uid || 'anon'}`;

export default function DailyActivitiesDashboard() {
  const { user, isAuthenticated, logout } = useAuth();
  const [dailyActivities, setDailyActivities] = useState(() => {
    try {
      const raw = sessionStorage.getItem(dailyDashboardSnapshotKey(auth?.currentUser?.uid));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.dailyActivities) ? parsed.dailyActivities : [];
    } catch (_) {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showBookView, setShowBookView] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [activityPageUrl, setActivityPageUrl] = useState(null);
  const [bookActivityPageUrls, setBookActivityPageUrls] = useState({});
  const [searchParams] = useSearchParams();
  const [showShareModal, setShowShareModal] = useState(false);
  const [activityToShare, setActivityToShare] = useState(null);
  const [shareStatus, setShareStatus] = useState({}); // { activityId: { isShared: boolean, submissionId: string } }

  // Activity page URLs will be loaded from Firestore activities collection
  const [activityPageUrls, setActivityPageUrls] = useState({});

  useEffect(() => {
    const activityUrls = (dailyActivities || []).flatMap((activity) => [
      activity?.imageUrl,
      activity?.artworkUrl,
      activity?.originalImageUrl,
      activity?.transformedImageUrl
    ]);
    const extraUrls = [
      activityPageUrl,
      ...Object.values(bookActivityPageUrls || {}),
      ...Object.values(activityPageUrls || {})
    ];
    warmResolvedImageUrls([...activityUrls, ...extraUrls].filter(Boolean), 36).catch(() => {});
  }, [dailyActivities, activityPageUrl, bookActivityPageUrls, activityPageUrls]);

  // Load activity page image when modal opens
  useEffect(() => {
    const loadActivityPage = async () => {
      if (selectedActivity && selectedActivity.activityId) {
        const activityId = selectedActivity.activityId;
        
        try {
          // Get activity data from Firestore to use the correct imageUrl
          const { dailyActivityService } = await import('../services/dailyActivityService');
          const allActivities = await dailyActivityService.getAllActivities();
          const activity = allActivities.find(a => a.id === activityId);
          
          if (activity && activity.imageUrl) {
            setActivityPageUrl(activity.imageUrl);
            console.log('✅ Loaded activity page URL for activity', activityId, ':', activity.imageUrl);
          } else {
            console.error('❌ Activity not found or no imageUrl for activity', activityId);
            setActivityPageUrl(null);
          }
        } catch (error) {
          console.error('❌ Error loading activity page:', error);
          setActivityPageUrl(null);
        }
      } else {
        setActivityPageUrl(null);
      }
    };

    if (showDetailsModal && selectedActivity) {
      loadActivityPage();
    } else {
      setActivityPageUrl(null);
    }
  }, [showDetailsModal, selectedActivity]);

  // Load all activity page URLs for book view
  useEffect(() => {
    const loadAllActivityPages = async () => {
      if (showBookView && dailyActivities.length > 0) {
        const urls = {};
        
        try {
          // Get all activities from Firestore
          const { dailyActivityService } = await import('../services/dailyActivityService');
          const allActivities = await dailyActivityService.getAllActivities();
          
          for (const activity of dailyActivities) {
            const activityId = activity.activityId;
            const firestoreActivity = allActivities.find(a => a.id === activityId);
            
            if (firestoreActivity && firestoreActivity.imageUrl && !urls[activityId]) {
              urls[activityId] = firestoreActivity.imageUrl;
              console.log('✅ Loaded activity page URL for book view - activity', activityId);
            }
          }
        } catch (error) {
          console.error('❌ Error loading activity pages for book view:', error);
        }
        
        setBookActivityPageUrls(urls);
      } else {
        setBookActivityPageUrls({});
      }
    };

    if (showBookView) {
      loadAllActivityPages();
    }
  }, [showBookView, dailyActivities]);

  useEffect(() => {
    if (auth.currentUser) {
      loadDailyActivities();
    }
  }, []);

  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    try {
      const raw = sessionStorage.getItem(dailyDashboardSnapshotKey(uid));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.dailyActivities) && parsed.dailyActivities.length > 0) {
        setDailyActivities(parsed.dailyActivities);
      }
    } catch (_) {}
  }, [auth?.currentUser?.uid]);

  // Load share status for all activities
  useEffect(() => {
    const loadShareStatus = async () => {
      if (!user || dailyActivities.length === 0) return;

      try {
        const statusMap = {};
        
        // Query all submissions for current user (more efficient than querying by activityId)
        const submissionsQuery = query(
          collection(db, 'submissions'),
          where('uid', '==', user.uid)
        );
        
        const snapshot = await getDocs(submissionsQuery);
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const activityId = data.activityId?.toString();
          if (activityId) {
            // Only track if this activity is in our daily activities list
            const activityExists = dailyActivities.some(a => a.activityId.toString() === activityId);
            if (activityExists) {
              statusMap[activityId] = {
                isShared: data.isPublic === true,
                submissionId: doc.id
              };
            }
          }
        });

        setShareStatus(statusMap);
        console.log('✅ Share status loaded:', statusMap);
      } catch (error) {
        console.error('❌ Error loading share status:', error);
      }
    };

    if (user && dailyActivities.length > 0) {
      loadShareStatus();
    }
  }, [user, dailyActivities]);

  // Check if activityId is in URL and open modal for that activity (only if coming from external source)
  useEffect(() => {
    const activityId = searchParams.get('activityId');
    if (activityId && dailyActivities.length > 0 && !showDetailsModal) {
      // Find the activity and open it in a modal
      const activity = dailyActivities.find(a => String(a.activityId) === String(activityId));
      if (activity) {
        console.log('🎯 Opening modal from URL parameter for activity:', activityId);
        setSelectedActivity(activity);
        setShowDetailsModal(true);
        // Automatically show feedback if it exists
        if (activity.feedback) {
          setShowFeedback(true);
        } else {
          setShowFeedback(false);
        }
        // Remove the activityId from URL to prevent re-triggering
        navigate('/daily-activities', { replace: true });
      }
    }
  }, [searchParams, dailyActivities, navigate, showDetailsModal]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showDetailsModal) {
        setShowDetailsModal(false);
        setSelectedActivity(null);
        setShowFeedback(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showDetailsModal]);

  // Reload activities when component becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && auth.currentUser) {
        console.log('🔄 Page visible, reloading daily activities...');
        loadDailyActivities();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also reload when window gains focus
    const handleFocus = () => {
      if (auth.currentUser) {
        console.log('🔄 Window focused, reloading daily activities...');
        loadDailyActivities();
      }
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadDailyActivities = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      
      if (!user) {
        console.log('❌ No user available for loadDailyActivities');
        setLoading(false);
        return;
      }
      
      console.log('🔍 [DEBUG] Loading daily activities for user:', user.uid);
      
      // Query specifically for daily activities - this is more efficient
      let dailyActivitiesQuery;
      try {
        // Try with orderBy first (more efficient if index exists)
        dailyActivitiesQuery = query(
          collection(db, 'userActivities'),
          where('userId', '==', user.uid),
          where('type', '==', 'daily_activity'),
          orderBy('createdAt', 'desc')
        );
        
        const snapshot = await getDocs(dailyActivitiesQuery);
        const dailyActivitiesRaw = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('✅ [DEBUG] Daily activities found (with orderBy):', dailyActivitiesRaw.length);
        
        // Sort activities by activityId in ascending order (1, 2, 3, etc.)
        const sortedDailyActivities = dailyActivitiesRaw.sort((a, b) => {
          const aId = parseInt(a.activityId) || 0;
          const bId = parseInt(b.activityId) || 0;
          return aId - bId;
        });

        // Group by activityId and keep the most recent entry for each activity
        const latestByActivityId = {};
        sortedDailyActivities.forEach(activity => {
          const key = parseInt(activity.activityId) || 0;
          const existing = latestByActivityId[key];
          const existingTime =
            (existing?.completedAt?.toMillis?.() || existing?.completedAt || 0) ||
            (existing?.createdAt?.toMillis?.() || existing?.createdAt || 0);
          const currentTime =
            (activity?.completedAt?.toMillis?.() || activity?.completedAt || 0) ||
            (activity?.createdAt?.toMillis?.() || activity?.createdAt || 0);
          
          if (!existing || currentTime > existingTime) {
            latestByActivityId[key] = activity;
          }
        });

        const uniqueActivities = Object.values(latestByActivityId);
        
        // FIX: Correct activity titles and descriptions to match activityId
        // If activityId is 1, title should be "Daily Activity 1", not "Daily Activity 33"
        const correctedActivities = uniqueActivities.map(activity => {
          const activityId = parseInt(activity.activityId) || activity.activityId;
          // Override title to match the correct activityId
          const correctedTitle = `Daily Activity ${activityId}`;
          // Override description to remove incorrect activity numbers (e.g., "Activity 33" -> "Activity 1")
          let correctedDescription = activity.description || 'Daily creative activity for developing artistic thinking';
          // Replace any "Activity XX" pattern in description with correct activityId
          correctedDescription = correctedDescription.replace(/Activity \d+/i, `Activity ${activityId}`);
          // If description doesn't have activity number, add it
          if (!correctedDescription.includes(`Activity ${activityId}`)) {
            correctedDescription = `Creative activity for developing artistic thinking - Activity ${activityId}`;
          }
          return {
            ...activity,
            title: correctedTitle, // Override with correct title based on activityId
            description: correctedDescription // Override description to match correct activityId
          };
        });
        
        console.log('✅ [DEBUG] Daily activities after grouping:', correctedActivities.length);
        console.log('✅ [DEBUG] Daily activities details (grouped):', correctedActivities.map(a => ({
          id: a.id,
          activityId: a.activityId,
          title: a.title,
          description: a.description,
          isCompleted: a.isCompleted,
          hasArtwork: !!a.transformedImageUrl
        })));
        
        setDailyActivities(correctedActivities);
        
      } catch (orderByError) {
        // If orderBy fails (no index), try without it
        console.warn('⚠️ OrderBy query failed, trying without orderBy:', orderByError.message);
        
        dailyActivitiesQuery = query(
          collection(db, 'userActivities'),
          where('userId', '==', user.uid),
          where('type', '==', 'daily_activity')
        );
        
        const snapshot = await getDocs(dailyActivitiesQuery);
        const dailyActivitiesRaw = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('✅ [DEBUG] Daily activities found (without orderBy):', dailyActivitiesRaw.length);
        
        // Sort activities by activityId in ascending order (1, 2, 3, etc.)
        const sortedDailyActivities = dailyActivitiesRaw.sort((a, b) => {
          const aId = parseInt(a.activityId) || 0;
          const bId = parseInt(b.activityId) || 0;
          return aId - bId;
        });
        
        // Group by activityId and keep the most recent entry for each activity
        const latestByActivityId = {};
        sortedDailyActivities.forEach(activity => {
          const key = parseInt(activity.activityId) || 0;
          const existing = latestByActivityId[key];
          const existingTime =
            (existing?.completedAt?.toMillis?.() || existing?.completedAt || 0) ||
            (existing?.createdAt?.toMillis?.() || existing?.createdAt || 0);
          const currentTime =
            (activity?.completedAt?.toMillis?.() || activity?.completedAt || 0) ||
            (activity?.createdAt?.toMillis?.() || activity?.createdAt || 0);
          
          if (!existing || currentTime > existingTime) {
            latestByActivityId[key] = activity;
          }
        });

        const uniqueActivities = Object.values(latestByActivityId);
        
        // FIX: Correct activity titles and descriptions to match activityId
        // If activityId is 1, title should be "Daily Activity 1", not "Daily Activity 33"
        const correctedActivities = uniqueActivities.map(activity => {
          const activityId = parseInt(activity.activityId) || activity.activityId;
          // Override title to match the correct activityId
          const correctedTitle = `Daily Activity ${activityId}`;
          // Override description to remove incorrect activity numbers (e.g., "Activity 33" -> "Activity 1")
          let correctedDescription = activity.description || 'Daily creative activity for developing artistic thinking';
          // Replace any "Activity XX" pattern in description with correct activityId
          correctedDescription = correctedDescription.replace(/Activity \d+/i, `Activity ${activityId}`);
          // If description doesn't have activity number, add it
          if (!correctedDescription.includes(`Activity ${activityId}`)) {
            correctedDescription = `Creative activity for developing artistic thinking - Activity ${activityId}`;
          }
          return {
            ...activity,
            title: correctedTitle, // Override with correct title based on activityId
            description: correctedDescription // Override description to match correct activityId
          };
        });
        
        console.log('✅ [DEBUG] Daily activities after grouping:', correctedActivities.length);
        console.log('✅ [DEBUG] Daily activities details (grouped):', correctedActivities.map(a => ({
          id: a.id,
          activityId: a.activityId,
          title: a.title,
          description: a.description,
          isCompleted: a.isCompleted,
          hasArtwork: !!a.transformedImageUrl
        })));
        
        setDailyActivities(correctedActivities);
      }
      
    } catch (error) {
      console.error('❌ Error loading daily activities:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    if (!Array.isArray(dailyActivities) || dailyActivities.length === 0) return;
    try {
      sessionStorage.setItem(
        dailyDashboardSnapshotKey(uid),
        JSON.stringify({ dailyActivities: dailyActivities.slice(0, 150) })
      );
    } catch (_) {}
  }, [auth?.currentUser?.uid, dailyActivities]);

  const handleDeleteActivity = async (activityId) => {
    const activity = dailyActivities.find(a => a.id === activityId);
    setActivityToDelete(activity);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!activityToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'userActivities', activityToDelete.id));
      setDailyActivities(prev => prev.filter(activity => activity.id !== activityToDelete.id));
      
      // Close modal if the deleted activity was selected
      if (selectedActivity && selectedActivity.id === activityToDelete.id) {
        setShowDetailsModal(false);
        setSelectedActivity(null);
        setShowFeedback(false);
      }
      
      // Close delete modal
      setShowDeleteModal(false);
      setActivityToDelete(null);
      
      // Show sophisticated success message
      setSuccessMessage('Activity deleted successfully!');
      setShowSuccessMessage(true);
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting activity:', error);
      setSuccessMessage('Failed to delete activity. Please try again.');
      setShowSuccessMessage(true);
      
      // Auto-hide after 4 seconds for errors
      setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 4000);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setActivityToDelete(null);
  };

  const handleShareActivity = async (activity, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Check if artwork exists
    if (!activity.userArtworkUrl && !activity.transformedImageUrl) {
      alert('Please upload artwork first before sharing to co-learning space.');
      return;
    }
    
    // Load activity page URL if not already loaded
    let activityPageUrlForShare = activityPageUrls[activity.activityId] || activity.activityPageUrl || activity.originalImageUrl;
    
    if (!activityPageUrlForShare) {
      try {
        const { dailyActivityService } = await import('../services/dailyActivityService');
        const allActivities = await dailyActivityService.getAllActivities();
        const firestoreActivity = allActivities.find(a => a.id === activity.activityId);
        
        if (firestoreActivity && firestoreActivity.imageUrl) {
          activityPageUrlForShare = firestoreActivity.imageUrl;
          setActivityPageUrls(prev => ({
            ...prev,
            [activity.activityId]: firestoreActivity.imageUrl
          }));
        }
      } catch (error) {
        console.error('Error loading activity page for share:', error);
      }
    }
    
    setActivityToShare({
      ...activity,
      activityPageUrlForShare
    });
    setShowShareModal(true);
  };

  const handleUnshareActivity = async (activity, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const activityId = activity.activityId.toString();
    const status = shareStatus[activityId];
    
    if (!status || !status.submissionId) {
      console.warn('No submission found to unshare');
      return;
    }

    if (!window.confirm('Are you sure you want to unshare this artwork from the co-learning space? You can share it again later.')) {
      return;
    }

    try {
      await updateDoc(doc(db, 'submissions', status.submissionId), {
        isPublic: false,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setShareStatus(prev => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          isShared: false
        }
      }));
      
      setSuccessMessage('Artwork unshared from co-learning space');
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error unsharing artwork:', error);
      setSuccessMessage('Failed to unshare artwork. Please try again.');
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 4000);
    }
  };

  const handleShareComplete = async (shouldShare, submissionId) => {
    if (activityToShare) {
      const activityId = activityToShare.activityId.toString();
      
      // Update share status
      setShareStatus(prev => ({
        ...prev,
        [activityId]: {
          isShared: shouldShare,
          submissionId: submissionId
        }
      }));
      
      setSuccessMessage(shouldShare ? 'Artwork shared to co-learning space!' : 'Artwork kept private');
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
    }
    
    setShowShareModal(false);
    setActivityToShare(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewFeedback = () => {
    setShowFeedback(!showFeedback);
  };

  const handlePrintBook = () => {
    // Navigate to checkout with the daily acts book data for printing
    navigate('/checkout', { 
      state: { 
        printMode: true,
        selectedBooks: [{
          id: 'daily-acts-book',
          title: 'My Daily Acts',
          image: dailyActivities.length > 0 ? getFullImageUrl(dailyActivities[0].originalImageUrl) : '/static/media/create-art.png',
          price: 400, // Daily acts book print cost
          type: 'print',
          author: 'Daily Activities Collection',
          scenes: dailyActivities.length,
          description: `Print your daily activities collection "My Daily Acts" with Artmosphere`,
          isDailyActs: true,
          dailyActivities: dailyActivities
        }]
      } 
    });
  };

  // Use centralized URL resolver
  const getFullImageUrl = (imageUrl) => {
    console.log('🔍 getFullImageUrl called with:', {
      imageUrl,
      type: typeof imageUrl,
      isString: typeof imageUrl === 'string',
      length: imageUrl?.length
    });
    
    if (!imageUrl || typeof imageUrl !== 'string') {
      console.warn('getFullImageUrl received invalid imageUrl:', imageUrl);
      return '/static/media/create-art.png';
    }
    
    return resolveImageUrl(imageUrl);
  };

  // Remove loading state - show page immediately with empty state if needed
  // if (loading) {
  //   return (
  //     <div className="artm-page artm-page-pad min-h-screen relative bg-[#FFF8E1]">
  //       <KidBackground variant="doodles" intensity={0.8} />
  //       <div className="relative max-w-7xl mx-auto p-6" style={{ zIndex: 10 }}>
  //         <div className="text-center py-12">
  //           <div className="text-6xl mb-4">🎨</div>
  //           <h3 className="text-2xl font-semibold text-slate-900 mb-2">Loading Daily Activities...</h3>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="artm-page artm-page-pad min-h-screen relative bg-[#FFF8E1] flex justify-center px-0 py-0 md:px-[2.5cm] md:py-8 lg:py-10">
      {/* Amber color splashes background - matching Learn Art History & My Art History */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>

      {/* Splash images merged into background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img
          src={splashImage}
          alt=""
          className="absolute top-10 right-5 opacity-20"
          style={{
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)',
          }}
        />
        <img
          src={splashImage}
          alt=""
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{
            width: '300px',
            height: '150px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)',
          }}
        />
      </div>

      <KidBackground variant="blobs" intensity={1} />
      
      {/* Window container — full bleed on mobile */}
      <div className="relative z-10 w-full max-w-full py-0 md:py-4">
        <div className="bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5 flex flex-col">
          {/* Compact header — Art History Learning style */}
          <header className="flex-shrink-0 sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
            <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px]">
              <p className="text-sm md:text-base font-semibold text-gray-800">
                Daily Activities
              </p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/my-art-world')}
                  className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                  title="Artist Corner"
                >
                  <LayoutDashboard className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Artist Corner</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/home')}
                  className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                  title="Home"
                >
                  <Home className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Home</span>
                </button>
              </div>
            </div>
          </header>

          {/* Compact second row: count + Book View */}
          <div className="flex items-center justify-between gap-3 py-2 border-b border-amber-100/80">
            <div className="flex items-center gap-2 text-slate-700">
              <FaCalendar className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-semibold">{dailyActivities.length} Activities</span>
            </div>
            {dailyActivities.length > 0 && (
              <button
                onClick={() => setShowBookView(true)}
                className="art-btn-primary px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-sm flex-shrink-0"
              >
                <FaBook className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">Book View</span>
                <span className="sm:hidden">Book</span>
              </button>
            )}
          </div>

        {/* Activities Grid */}
        {dailyActivities.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-2xl font-semibold text-slate-900 mb-2">No Daily Activities Yet</h3>
            <p className="text-slate-700 mb-6">Start your creative journey with daily art activities!</p>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🎯 Start Daily Activities button clicked');
                console.log('🎯 Navigating to /daily-art-activity');
                try {
                  navigate('/daily-art-activity');
                  console.log('✅ Navigation called successfully');
                } catch (error) {
                  console.error('❌ Navigation error:', error);
                  // Fallback: use window.location
                  window.location.href = '/daily-art-activity';
                }
              }}
              style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
              className="art-btn-primary rounded-full px-6 py-3 font-semibold transition"
            >
              Start Daily Activities
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {dailyActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-xl border-2 border-amber-300 hover:border-amber-400 transition-all duration-300 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Open modal popup with activity details
                    console.log('🎯 Opening modal for activity:', activity.activityId);
                    console.log('📊 Activity data:', {
                      id: activity.id,
                      activityId: activity.activityId,
                      title: activity.title,
                      originalImageUrl: activity.originalImageUrl,
                      activityPageUrl: activity.activityPageUrl,
                      transformedImageUrl: activity.transformedImageUrl,
                      userArtworkUrl: activity.userArtworkUrl,
                      hasFeedback: !!activity.feedback
                    });
                    setSelectedActivity(activity);
                    setShowDetailsModal(true);
                    // Automatically show feedback if it exists
                    if (activity.feedback) {
                      setShowFeedback(true);
                    } else {
                  setShowFeedback(false);
                    }
                  }}
                >
                  {/* Activity Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {activity.activityId}
                      </div>
                      <span className="text-amber-800 font-semibold text-sm">
                        Activity {activity.activityId}
                      </span>
                    </div>
                    
                                       {/* Action Buttons */}
                    <div className="flex flex-col gap-1">
                      {/* Share/Unshare Button */}
                      {activity.userArtworkUrl || activity.transformedImageUrl ? (
                        shareStatus[activity.activityId]?.isShared ? (
                          <button
                            onClick={(e) => handleUnshareActivity(activity, e)}
                            className="art-btn-primary p-1.5 rounded-full transition-colors"
                            title="Unshare from Co-Learning Space"
                          >
                            <FaShareAlt size={12} />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => handleShareActivity(activity, e)}
                            className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded-full transition-colors"
                            title="Share to Co-Learning Space"
                          >
                            <FaShare size={12} />
                          </button>
                        )
                      ) : null}
                      
                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteActivity(activity.id);
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full transition-colors"
                        title="Delete Activity"
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Artwork Thumbnail */}
                  <div className="relative mb-3">
                  <div className="w-full h-32 rounded-lg border-2 border-amber-200 overflow-hidden bg-amber-50">
                      <SmartImage
                      src={getFullImageUrl(activity.transformedImageUrl || activity.userArtworkUrl)} 
                        alt="Your artwork" 
                        className="w-full h-full object-cover pointer-events-none"
                        containerClassName="w-full h-full"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onError={(e) => {
                        console.log('❌ Card image failed to load:', e.currentTarget.src);
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.parentElement.querySelector('.image-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      {/* Fallback when image fails to load */}
                      <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                        <div className="text-center">
                        <div className="text-3xl mb-1">🎨</div>
                        <p className="text-xs font-medium text-amber-700">Your Artwork</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Activity Info */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-amber-800 text-sm">
                      {activity.title || `Daily Art Activity ${activity.activityId}`}
                  </h4>
                  <p className="text-amber-600 text-xs line-clamp-2">
                    {activity.description || 'Daily creative activity for developing artistic thinking'}
                  </p>
                  <div className="text-xs text-amber-500">
                    {formatDate(activity.completedAt || activity.createdAt)}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity Details Modal */}
      {showDetailsModal && selectedActivity && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking on backdrop
            if (e.target === e.currentTarget) {
              setShowDetailsModal(false);
              setSelectedActivity(null);
              setShowFeedback(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-amber-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <h2 className="text-xl font-semibold text-amber-800">
                Daily Art Activity {selectedActivity.activityId}
              </h2>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetailsModal(false);
                  setSelectedActivity(null);
                  setShowFeedback(false);
                }}
                className="text-amber-600 hover:text-amber-800 text-xl font-bold transition-colors cursor-pointer"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-auto max-h-[70vh]">
              {/* Original Layout: Activity Page on Left, Artwork on Right */}
              <div className="flex justify-center mb-6">
                <div className="w-full max-w-3xl">
                  <div className="relative w-full rounded-xl border-2 border-amber-200 shadow-lg overflow-hidden bg-white">
                    {/* Background: Activity Page Image (Left Side) */}
                    <div className="relative w-full" style={{ minHeight: '400px' }}>
                      {/* Activity Page Image (Left Side) - Load from Firebase Storage */}
                      {activityPageUrl ? (
                        <SmartImage
                          key={`activity-bg-${selectedActivity.id}-${activityPageUrl}`}
                          src={activityPageUrl} 
                        alt="Activity page background" 
                        className="w-full h-auto object-contain"
                          containerClassName="w-full h-auto"
                          style={{ display: 'block', width: '100%' }}
                        onError={(e) => {
                            console.error('❌ Activity page background failed to load:', e.currentTarget.src);
                          e.currentTarget.style.display = 'none';
                        }}
                          onLoad={() => {
                            console.log('✅ Activity page background loaded from Firebase Storage');
                          }}
                        />
                      ) : selectedActivity.originalImageUrl || selectedActivity.activityPageUrl ? (
                        <SmartImage
                          key={`activity-bg-${selectedActivity.id}`}
                          src={getFullImageUrl(selectedActivity.originalImageUrl || selectedActivity.activityPageUrl)} 
                          alt="Activity page background" 
                          className="w-full h-auto object-contain"
                          containerClassName="w-full h-auto"
                          style={{ display: 'block', width: '100%' }}
                          onError={(e) => {
                            console.error('❌ Activity page background failed to load:', e.currentTarget.src);
                            e.currentTarget.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('✅ Activity page background loaded from stored URL');
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                          <div className="text-center">
                            <div className="text-4xl mb-2">🎨</div>
                            <p className="text-sm font-medium text-amber-700">Loading Activity Page...</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Overlay: User's Artwork - Positioned on the right side */}
                      {selectedActivity.transformedImageUrl || selectedActivity.userArtworkUrl ? (
                        <div 
                          className="absolute top-0 right-0 w-1/2 h-full pointer-events-none"
                          style={{ zIndex: 10 }}
                        >
                          <SmartImage
                            key={`user-artwork-${selectedActivity.id}`}
                            src={getFullImageUrl(selectedActivity.transformedImageUrl || selectedActivity.userArtworkUrl)} 
                            alt="Your artwork overlay" 
                            className="w-full h-full object-cover"
                            containerClassName="w-full h-full"
                            style={{
                              mixBlendMode: 'multiply',
                              objectFit: 'cover',
                              objectPosition: 'center'
                            }}
                            onLoad={(e) => {
                              console.log('✅ User artwork overlay loaded successfully');
                            }}
                      onError={(e) => {
                              console.error('❌ User artwork overlay failed to load:', e.currentTarget.src);
                        e.currentTarget.style.display = 'none';
                              // Show placeholder for user artwork
                              const placeholder = e.currentTarget.parentElement;
                              if (placeholder && !placeholder.querySelector('.artwork-placeholder')) {
                                const placeholderDiv = document.createElement('div');
                                placeholderDiv.className = 'artwork-placeholder w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 opacity-50';
                                placeholderDiv.innerHTML = `
                                  <div class="text-center">
                                    <div class="text-2xl mb-1">🎨</div>
                                    <p class="text-xs font-medium text-amber-700">Your Artwork</p>
                                  </div>
                                `;
                                placeholder.appendChild(placeholderDiv);
                              }
                      }}
                    />
                        </div>
                      ) : (
                        <div className="absolute top-0 right-0 w-1/2 h-full flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 opacity-50 pointer-events-none">
                      <div className="text-center">
                            <div className="text-2xl mb-1">🎨</div>
                            <p className="text-xs font-medium text-amber-700">Your Artwork</p>
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                </div>
                      </div>

              {/* View Feedback Button - Only show if feedback exists */}
              {selectedActivity.feedback && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={handleViewFeedback}
                    className="bg-white hover:bg-amber-50 text-amber-700 border border-amber-300 px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                  >
                    {showFeedback ? 'Hide' : 'View'} Feedback
                  </button>
                        </div>
                      )}

              {/* Feedback Section - Automatically shown if feedback exists, can be toggled */}
              {showFeedback && selectedActivity.feedback && (
                <div className="mt-6 bg-gradient-to-br from-amber-50 via-amber-100 to-orange-50 rounded-2xl p-8 border border-amber-200 shadow-xl">
                  <h4 className="text-3xl font-bold text-amber-800 mb-8 text-center tracking-wide">Artmosphere Feedback</h4>
                  
                  <div className="space-y-6">
                    {/* Overall Message */}
                    {selectedActivity.feedback.overallMessage && (
                      <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 border-l-4 border-amber-400 shadow-lg">
                        <h5 className="text-xl font-semibold text-amber-700 mb-4">Overall Message</h5>
                        <p className="text-amber-800 leading-relaxed text-lg">{selectedActivity.feedback.overallMessage}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Strengths */}
                        {selectedActivity.feedback.strengths && selectedActivity.feedback.strengths.length > 0 && (
                        <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 border-l-4 border-amber-500 shadow-lg">
                          <h5 className="text-xl font-semibold text-amber-700 mb-4">Strengths</h5>
                          <ul className="space-y-3">
                              {selectedActivity.feedback.strengths.map((strength, index) => (
                              <li key={index} className="text-amber-800 leading-relaxed flex items-start">
                                <span className="text-amber-500 mr-3 text-lg">•</span>
                                <span className="text-amber-800">{strength}</span>
                              </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      
                      {/* Improvements */}
                        {selectedActivity.feedback.improvements && selectedActivity.feedback.improvements.length > 0 && (
                        <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 border-l-4 border-orange-400 shadow-lg">
                          <h5 className="text-xl font-semibold text-orange-700 mb-4">Areas to Explore</h5>
                          <ul className="space-y-3">
                              {selectedActivity.feedback.improvements.map((improvement, index) => (
                              <li key={index} className="text-amber-800 leading-relaxed flex items-start">
                                <span className="text-amber-500 mr-3 text-lg">•</span>
                                <span className="text-amber-800">{improvement}</span>
                              </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                          </div>
                        )}
            </div>
          </div>
                          </div>
                        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && activityToDelete && (
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg max-w-sm w-full mx-4 border border-amber-200">
              {/* Content */}
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaTrash size={24} className="text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Delete Activity?</h3>
                  <p className="text-amber-600 text-sm">
                    This action cannot be undone
                  </p>
                </div>

                <div className="bg-amber-50 rounded-lg p-4 mb-6 border border-amber-100">
                  <p className="font-medium text-amber-800 mb-1">
                    {activityToDelete.title || `Activity ${activityToDelete.activityId}`}
                  </p>
                  <p className="text-sm text-amber-600">
                    Created {formatDate(activityToDelete.createdAt)}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={cancelDelete}
                    className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-700 px-4 py-2.5 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex-1 art-btn-primary px-4 py-2.5 rounded-lg font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
                          </div>
                        )}

        {/* Book View Modal - Mobile Optimized */}
        {showBookView && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4" style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}>
            <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] md:max-h-[90vh] flex flex-col relative">
              {/* Header - Mobile Optimized */}
              <div className="art-btn-primary p-3 md:p-6 rounded-t-xl md:rounded-t-2xl flex-shrink-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                    <FaBook className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                    <div className="min-w-0">
                      <h3 className="text-lg md:text-2xl font-bold truncate">My Daily Acts</h3>
                      <p className="text-xs md:text-base text-amber-100">Page {currentPage + 1} of {dailyActivities.length + 1}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 md:gap-3 flex-shrink-0">
                    <button
                      onClick={() => handlePrintBook()}
                      className="bg-white/20 hover:bg-white/30 text-white px-2 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-colors flex items-center gap-1 md:gap-2 text-xs md:text-base"
                    >
                      <FaPrint className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Print Book</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowBookView(false);
                        setCurrentPage(0);
                      }}
                      className="bg-white/20 hover:bg-white/30 text-white p-1.5 md:p-2 rounded-lg transition-colors flex-shrink-0"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Book Content - Fixed Navigation Arrows */}
              <div className="flex-1 relative overflow-hidden">
                {/* Page Navigation - Fixed Position */}
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="fixed left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-amber-700 p-2 md:p-3 rounded-full shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed z-50 border-2 border-amber-200"
                  style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
                >
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => setCurrentPage(Math.min(dailyActivities.length, currentPage + 1))}
                  disabled={currentPage === dailyActivities.length}
                  className="fixed right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-amber-700 p-2 md:p-3 rounded-full shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed z-50 border-2 border-amber-200"
                  style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
                >
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Page Content with Scroller */}
                <div className="w-full max-w-4xl mx-auto p-4 md:p-8 overflow-y-auto h-full" style={{ maxHeight: 'calc(95vh - 100px)', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
                    {currentPage === 0 ? (
                      /* Cover Page */
                      <div className="bg-gradient-to-br from-amber-50 via-amber-100 to-orange-50 rounded-2xl p-16 text-center border border-amber-200 shadow-xl">
                        <div className="mb-12">
                          <h1 className="text-6xl font-bold text-amber-800 mb-4 tracking-wide">
                            My Daily Acts
                          </h1>
                          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-400 mx-auto mb-6"></div>
                          <p className="text-xl text-amber-700 font-light tracking-wide">
                            A Collection of Creative Activities
                          </p>
                        </div>
                        
                        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 inline-block border border-amber-100 shadow-lg">
                          <p className="text-amber-700 font-medium text-lg mb-2">
                            Created on {new Date().toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                          <p className="text-amber-600 text-lg">
                            {dailyActivities.length} Activities Completed
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* Activity Page - Mobile Optimized */
                      dailyActivities[currentPage - 1] ? (
                      <div className="bg-white rounded-xl md:rounded-2xl border-2 border-amber-200 shadow-lg">
                        <div className="w-full">
                          {/* Single Combined Image View - Activity Page with User Artwork Overlay */}
                            <div 
                              className="w-full relative" 
                              id={`book-activity-container-${dailyActivities[currentPage - 1].activityId}`}
                              style={{ minHeight: '400px' }}
                            >
                              {/* Activity Page as Background - Load from Firebase Storage */}
                              {bookActivityPageUrls[dailyActivities[currentPage - 1].activityId] ? (
                                <SmartImage
                                  id={`book-activity-bg-${dailyActivities[currentPage - 1].activityId}`}
                                  src={bookActivityPageUrls[dailyActivities[currentPage - 1].activityId]} 
                              alt="Activity page"
                              className="w-full h-auto object-contain"
                                  containerClassName="w-full h-auto"
                                  style={{ display: 'block', position: 'relative', zIndex: 1, width: '100%' }}
                              onError={(e) => {
                                console.log('❌ Activity page failed to load:', e.currentTarget.src);
                                e.currentTarget.style.display = 'none';
                              }}
                                  onLoad={(e) => {
                                    console.log('✅ Activity page loaded for book view, height:', e.currentTarget.offsetHeight || e.currentTarget.naturalHeight);
                                    // Update container height to match image and ensure overlay is visible
                                    const container = e.currentTarget.parentElement;
                                    if (container) {
                                      const imgHeight = e.currentTarget.offsetHeight || e.currentTarget.naturalHeight || 600;
                                      container.style.height = imgHeight + 'px';
                                      container.style.minHeight = imgHeight + 'px';
                                      console.log('📏 Container height set to:', imgHeight);
                                      
                                      // Update overlay height as well
                                      const overlay = container.querySelector(`#book-artwork-overlay-${dailyActivities[currentPage - 1].activityId}`);
                                      if (overlay) {
                                        overlay.style.height = imgHeight + 'px';
                                        overlay.style.minHeight = imgHeight + 'px';
                                        console.log('📏 Overlay height set to:', imgHeight);
                                      }
                                    }
                                  }}
                                />
                              ) : dailyActivities[currentPage - 1].originalImageUrl || dailyActivities[currentPage - 1].activityPageUrl ? (
                                <SmartImage
                                  id={`book-activity-bg-${dailyActivities[currentPage - 1].activityId}`}
                                  src={getFullImageUrl(dailyActivities[currentPage - 1].originalImageUrl || dailyActivities[currentPage - 1].activityPageUrl)} 
                                  alt="Activity page"
                                  className="w-full h-auto object-contain"
                                  containerClassName="w-full h-auto"
                                  style={{ display: 'block', position: 'relative', zIndex: 1, width: '100%' }}
                                  onError={(e) => {
                                    console.log('❌ Activity page failed to load:', e.currentTarget.src);
                                    e.currentTarget.style.display = 'none';
                                  }}
                                  onLoad={(e) => {
                                    // Update container height to match image
                                    const container = e.currentTarget.parentElement;
                                    if (container) {
                                      const imgHeight = e.currentTarget.offsetHeight || e.currentTarget.naturalHeight || 600;
                                      container.style.height = imgHeight + 'px';
                                      container.style.minHeight = imgHeight + 'px';
                                      
                                      // Update overlay height as well
                                      const overlay = container.querySelector(`#book-artwork-overlay-${dailyActivities[currentPage - 1].activityId}`);
                                      if (overlay) {
                                        overlay.style.height = imgHeight + 'px';
                                        overlay.style.minHeight = imgHeight + 'px';
                                      }
                                    }
                                  }}
                                />
                              ) : (
                                <div className="w-full h-96 flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                                  <div className="text-center">
                                    <div className="text-4xl mb-2">🎨</div>
                                    <p className="text-sm font-medium text-amber-700">Loading Activity Page...</p>
                                  </div>
                                </div>
                              )}
                            
                            {/* User Artwork Overlay on Right Half */}
                              {(() => {
                                const currentActivity = dailyActivities[currentPage - 1];
                                const artworkUrl = currentActivity?.transformedImageUrl || currentActivity?.userArtworkUrl;
                                console.log('🔍 Book View - Activity:', currentActivity?.activityId, {
                                  activity: currentActivity,
                                  hasTransformedImageUrl: !!currentActivity?.transformedImageUrl,
                                  hasUserArtworkUrl: !!currentActivity?.userArtworkUrl,
                                  transformedImageUrl: currentActivity?.transformedImageUrl,
                                  userArtworkUrl: currentActivity?.userArtworkUrl,
                                  artworkUrl: artworkUrl ? artworkUrl.substring(0, 100) : 'none',
                                  fullArtworkUrl: artworkUrl
                                });
                                
                                if (!artworkUrl) {
                                  return (
                                    <div className="absolute top-0 right-0 w-1/2 h-full flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 opacity-30 pointer-events-none" style={{ minHeight: '600px' }}>
                                      <div className="text-center">
                                        <div className="text-2xl mb-1">🎨</div>
                                        <p className="text-xs font-medium text-amber-700">No artwork</p>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return (
                                  <div 
                                    id={`book-artwork-overlay-${currentActivity?.activityId}`}
                                    className="absolute top-0 right-0 w-1/2"
                                style={{
                                  height: '100%',
                                      minHeight: '600px',
                                      overflow: 'hidden',
                                      zIndex: 10,
                                      pointerEvents: 'none',
                                      backgroundColor: 'transparent'
                                }}
                              >
                                <SmartImage
                                      key={`book-artwork-img-${currentActivity?.activityId}-${artworkUrl}`}
                                      src={getFullImageUrl(artworkUrl)} 
                                  alt="User artwork overlay"
                                  className="w-full h-full object-cover"
                                  containerClassName="w-full h-full"
                                  style={{
                                        mixBlendMode: 'multiply',
                                    objectFit: 'cover',
                                        objectPosition: 'center',
                                        display: 'block',
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '600px'
                                  }}
                                  onLoad={(e) => {
                                        console.log('✅ User artwork overlay loaded successfully in book view:', e.currentTarget.src);
                                        // Ensure parent container has proper height
                                        const overlay = e.currentTarget.parentElement;
                                        const container = overlay?.parentElement;
                                        if (container && overlay) {
                                          const bgImg = container.querySelector(`#book-activity-bg-${currentActivity?.activityId}`);
                                          if (bgImg) {
                                            const imgHeight = bgImg.offsetHeight || bgImg.naturalHeight || 600;
                                            container.style.height = imgHeight + 'px';
                                            overlay.style.height = imgHeight + 'px';
                                            e.currentTarget.style.height = imgHeight + 'px';
                                            console.log('📏 Updated heights after artwork load:', imgHeight);
                                          }
                                        }
                                  }}
                                  onError={(e) => {
                                        console.error('❌ User artwork overlay failed to load in book view:', e.currentTarget.src);
                                    e.currentTarget.style.display = 'none';
                                        // Show placeholder
                                        const overlay = e.currentTarget.parentElement;
                                        if (overlay && !overlay.querySelector('.artwork-error-placeholder')) {
                                          const placeholder = document.createElement('div');
                                          placeholder.className = 'artwork-error-placeholder absolute inset-0 flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 opacity-50';
                                          placeholder.innerHTML = `
                                            <div class="text-center">
                                              <div class="text-2xl mb-1">🎨</div>
                                              <p class="text-xs font-medium text-amber-700">Artwork unavailable</p>
                                            </div>
                                          `;
                                          overlay.appendChild(placeholder);
                                        }
                                  }}
                                />
                          </div>
                                );
                              })()}
                          </div>
                      </div>
                    </div>
                      ) : (
                        <div className="w-full h-96 flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                          <div className="text-center">
                            <div className="text-4xl mb-2">🎨</div>
                            <p className="text-sm font-medium text-amber-700">No activity found</p>
                          </div>
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && activityToShare && (
          <LearningSpaceShareModal
            isOpen={showShareModal}
            onClose={() => {
              setShowShareModal(false);
              setActivityToShare(null);
            }}
            activityId={activityToShare.activityId}
            imageUrl={activityToShare.userArtworkUrl || activityToShare.transformedImageUrl}
            activityData={{
              title: activityToShare.title || `Daily Art Activity ${activityToShare.activityId}`,
              description: activityToShare.description || 'Daily creative activity for developing artistic thinking',
              prerequisites: activityToShare.metadata?.prerequisites || 'Art supplies and imagination',
              steps: activityToShare.metadata?.steps || 'Follow the activity instructions and create your artwork'
            }}
            activityPageUrl={activityToShare.activityPageUrlForShare || activityPageUrls[activityToShare.activityId] || activityToShare.activityPageUrl || activityToShare.originalImageUrl}
            onShareComplete={handleShareComplete}
          />
        )}

        {/* Sophisticated Success/Error Notification */}
        {showSuccessMessage && (
          <div className="fixed top-6 right-6 z-50 animate-in slide-in-from-right duration-300">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-amber-200 p-4 max-w-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800 text-sm">
                    {successMessage}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowSuccessMessage(false);
                    setSuccessMessage('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}







